/**
 * IMBEE-8069 (T86) UAT — which contact fields the widget's plain text input can carry.
 *
 * The contact form renders through a chain of if/else branches on the field's type, and that
 * chain has no final else. Only 'text' and 'textarea' reached the text input, so a field of any
 * other type returned undefined and rendered NOTHING — no control, no label, no error.
 *
 * The types that actually reach the widget are fixed by core-php, which only forwards fields
 * whose type is in WebChat::getAvailableContactFieldTypes():
 *
 *     text | select | radio | checkbox | textarea | date | time
 *
 * (web-chat-services' ContactFieldType enum is the same seven, and rejects anything else.) The
 * chain has dedicated branches for select, radio and checkbox — so of the seven, DATE and TIME
 * fell through and rendered nothing at all. A visitor cannot fill in a field that is not on
 * screen, so anything left over now falls back to a text input: a date the visitor types by
 * hand beats a field that silently is not there.
 */
import { OptionFieldType } from '../store/types';

/** Fields the phone control claims, matched on label rather than type, as it always has been. */
const PHONE_LABEL = /Phone/i;

/**
 * Types the render chain handles with a dedicated control BEFORE it consults this predicate.
 *
 * Listing them here is redundant with the chain today — the predicate is only reached after
 * those branches, so it is never asked about them. It is deliberate insurance: without it the
 * predicate answers `true` for `select`, and reordering the chain (the one edit that would turn
 * every dropdown into a text box) would pass every test silently. With it, the predicate means
 * what its name says on its own, and the tests below can assert that instead of asserting the
 * opposite and explaining why in a comment.
 */
const HAS_DEDICATED_CONTROL = ['select', 'radio', 'checkbox'];

export const isFreeTextField = (optionField: OptionFieldType): boolean =>
    !HAS_DEDICATED_CONTROL.includes(optionField.type) &&
    // Parenthesised deliberately: && binds tighter than ||, so without these brackets the
    // exclusion above would apply only to the first disjunct and `select` would still come back
    // true. The tests below catch exactly that.
    (
        // text and textarea are listed explicitly, ahead of the label check, because they
        // already won over the phone branch before this change. Folding them into the fallback
        // would hand every existing text field labelled 'Phone' to the phone input, which
        // submits through values.phone instead of option{N} — a silent change to what those
        // channels post.
        optionField.type === 'text' ||
        optionField.type === 'textarea' ||
        // The empty-type guard matters: optionFields is padded to three placeholder entries
        // with type '', and those must keep rendering nothing.
        (Boolean(optionField.type) && !PHONE_LABEL.test(optionField.label))
    );
